import {
  BarChart3,
  CircleHelp,
  DollarSign,
  LayoutDashboard,
  MessageCircle,
  MonitorSmartphone,
  Package,
  Settings2,
  Shield,
  Smartphone,
  Truck,
  Warehouse,
  Workflow,
} from 'lucide-react';

export type GuideCallout = {
  x: number;
  y: number;
  title: string;
  text: string;
};

export type GuideVisual = {
  image: string;
  imageAlt: string;
  caption: string;
  callouts?: GuideCallout[];
};

export type GuideSection = {
  id: string;
  number: string;
  title: string;
  icon: any;
  summary: string;
  audience: string[];
  paths: string[];
  modules: string[];
  tasks: string[];
  support: string[];
  visuals?: GuideVisual[];
};

export const guideSections: GuideSection[] = [
  {
    id: 'access-roles',
    number: '01',
    title: 'Erisim, Giris ve Roller',
    icon: Shield,
    summary: 'Sisteme giris, oturum acilisi, rol mantigi ve kullanicinin hangi menuye neden eristiginin temel aciklamasidir.',
    audience: ['Admin', 'Dispozisyon Uzmanı', 'Depo', 'Help desk', 'Tum masaustu kullanicilar'],
    paths: ['/login', '/driver-mobile-auth', '/driver-mobile-register'],
    modules: ['Web giris', 'Mobil surucu girisi', 'Rol bazli yetki yapisi', 'Yeni surucu kayit akisi'],
    tasks: ['Kullanicinin dogru hesapla giris yaptigini dogrulayin.', 'Rol ve yetki eslesmesini kontrol edin.', 'Yeni kayit olan surucunun dogru akisa yonlendigini teyit edin.'],
    support: ['Giris sorunu varsa hesap aktifligi, sifre ve rol bilgisi kontrol edilir.', 'Menu gorunmeme sorunu cogu zaman rol veya yetki kaynaklidir.'],
    visuals: [
      {
        image: '/help-guide/01-login.png',
        imageAlt: 'Web giris ekrani',
        caption: 'Web giris ekrani',
        callouts: [
          { x: 66, y: 34, title: '1', text: 'Kullanici e-posta adresi bu alandan girilir.' },
          { x: 66, y: 49, title: '2', text: 'Sifre dogrulanir. Hatali denemelerde kullanici statusu da kontrol edilmelidir.' },
          { x: 66, y: 67, title: '3', text: 'Giris butonu basarili oturumdan sonra kullaniciyi yetkili ana ekrana tasir.' },
        ],
      },
      {
        image: '/help-guide/06-mobile-auth.png',
        imageAlt: 'Mobil giris ekrani',
        caption: 'Mobil surucu giris akisi',
      },
    ],
  },
  {
    id: 'dashboard-navigation',
    number: '02',
    title: 'Anasayfa, Dashboard ve Menu Yapisi',
    icon: LayoutDashboard,
    summary: 'Anasayfa operasyonel gorunurluk saglar; sol menu ise tum ana modullerin giris kapisidir.',
    audience: ['Admin', 'Dispozisyon Uzmanı', 'Depo', 'Yonetim'],
    paths: ['/', '/live-tracking', '/documents', '/profile', '/settings'],
    modules: ['Anasayfa', 'Canli Takip', 'Belgeler', 'Profilim', 'Sistem Ayarlari'],
    tasks: ['Gunluk operasyon yogunlugunu takip edin.', 'Bildirimleri ust alandan kontrol edin.', 'Menu gruplarindan ilgili modulu acin.'],
    support: ['Kullanici ana ekrani goruyorsa oturum ve temel yetki akisi calisiyor demektir.', 'Bildirim tiklandiginda ilgili ekran acilmiyorsa yonlendirme veya veri iliskisi kontrol edilmelidir.'],
    visuals: [
      {
        image: '/help-guide/02-dashboard-menu.png',
        imageAlt: 'Dashboard ve menu ekran goruntusu',
        caption: 'Anasayfa ve ana menu',
        callouts: [
          { x: 15, y: 28, title: '1', text: 'Sol menu tum ana modul gruplarini icerir.' },
          { x: 75, y: 14, title: '2', text: 'Header alaninda bildirim ve kullanici bilgisi yer alir.' },
          { x: 64, y: 32, title: '3', text: 'Dashboard kartlari gunluk operasyon ozetini sunar.' },
        ],
      },
      {
        image: '/help-guide/15-live-tracking.png',
        imageAlt: 'Canli takip goruntusu',
        caption: 'Canli takip penceresi',
      },
      {
        image: '/help-guide/14-documents.png',
        imageAlt: 'Belgeler ekrani goruntusu',
        caption: 'Belgeler penceresi',
      },
    ],
  },
  {
    id: 'operations',
    number: '03',
    title: 'Operasyon ve Sevkiyat Yonetimi',
    icon: Package,
    summary: 'Operasyon yonetimi bolumu siparis, sevkiyat, teklif talebi, rampa atama ve bekleme kayitlarinin merkezidir.',
    audience: ['Dispozisyon Uzmanı', 'Operasyon', 'Depo', 'Help desk'],
    paths: ['/tracking', '/offer-requests', '/warehouse-ramp-assignment', '/waiting-records', '/shipment-monitoring', '/driver-tracking'],
    modules: ['Siparis Yonetimi', 'Teklif Taleplerim', 'Rampa Atama', 'Bekleme Kayitlari', 'Sevkiyat Izleme', 'Surucu Takip'],
    tasks: ['Kaydi filtreleyip bulun.', 'Yukleme ve teslimat noktalarini kontrol edin.', 'Arac, surucu, durum ve not alanlarini guncelleyin.', 'Bekleme ve saha gecikmelerini kayda alin.'],
    support: ['Liste bos geliyorsa once filtre ve tarih araligi sifirlanir.', 'Rota veya durak bilgisinde uyusmazlik varsa siparis detayi ile takip detayi birlikte incelenir.'],
    visuals: [
      {
        image: '/help-guide/03-operations.png',
        imageAlt: 'Operasyon ekran goruntusu',
        caption: 'Operasyon yonetimi ana penceresi',
        callouts: [
          { x: 17, y: 18, title: '1', text: 'Sol menu icinden operasyon alt ekranlari secilir.' },
          { x: 52, y: 27, title: '2', text: 'Filtre ve arama alanlari kayda hizli ulasmak icin kullanilir.' },
          { x: 72, y: 55, title: '3', text: 'Satir veya kart detayindan sevkiyat uzerinde aksiyon alinir.' },
        ],
      },
      {
        image: '/help-guide/16-warehouse-ramp.png',
        imageAlt: 'Rampa atama goruntusu',
        caption: 'Rampa atama penceresi',
      },
    ],
  },
  {
    id: 'fleet-personnel',
    number: '04',
    title: 'Arac ve Personel Yonetimi',
    icon: Truck,
    summary: 'Cekici, dorse, vardiya, surucu ve dis surucu basvuru surecleri bu katmanda yonetilir.',
    audience: ['Admin', 'Operasyon', 'IK destek', 'Help desk'],
    paths: ['/vehicle-management', '/trailer-management', '/shift-management', '/shift-assignments', '/drivers', '/driver-applications', '/approved-drivers', '/driver-leave-requests', '/vehicle-assignments'],
    modules: ['Cekici Yonetimi', 'Dorse Yonetimi', 'Vardiya Tanimlari', 'Vardiya Atamalari', 'Ozmal Suruculerimiz', 'Spot Surucu Basvurulari', 'Spot Suruculer', 'Surucu Izin Talepleri', 'Arac Atamalari'],
    tasks: ['Arac karti ve plaka kayitlarini guncelleyin.', 'Surucu belgelerini ve durumunu inceleyin.', 'Vardiya tanimlayip atama yapin.', 'Basvuru veya izin talebini degerlendirin.'],
    support: ['Surucu listesinde eksik kayit goruluyorsa ilgili kullanici ile surucu eslesmesi kontrol edilir.', 'Atama sorunlarinda vardiya, arac ve kullanici durum alanlari birlikte incelenir.'],
    visuals: [
      {
        image: '/help-guide/08-fleet-personnel.png',
        imageAlt: 'Surucu ve personel yonetimi goruntusu',
        caption: 'Surucu ve personel yonetimi penceresi',
      },
    ],
  },
  {
    id: 'customer-warehouse',
    number: '05',
    title: 'Musteri, Depo, Rampa ve Fiyat Tanimlari',
    icon: Warehouse,
    summary: 'Musteri ve depo master verileri ile rampa ve baz fiyat tanimlari operasyonun dogru hesaplanmasi icin kritik onemdedir.',
    audience: ['Admin', 'Operasyon', 'Depo'],
    paths: ['/customer-management', '/warehouse-settings', '/ramp-management', '/base-prices', '/capacity', '/warehouse-transfer'],
    modules: ['Musteriler', 'Depo Tanimlari', 'Rampa Tanimlari', 'Baz Fiyatlari', 'Kapasite Planlama', 'Depo Transferi'],
    tasks: ['Musteri ve depo kayitlarini acin.', 'Adres, sehir, ilce ve konum verilerini dogrulayin.', 'Rampa kapasitesi ve kullanim bilgisini yonetin.', 'Baz fiyat rotalarini guncel tutun.'],
    support: ['Hatali fiyat veya rota eslesmelerinde baz fiyat ve depo secimleri kontrol edilir.', 'Depo veya musteri listesi bos ise aktiflik ve filtreler incelenir.'],
    visuals: [
      {
        image: '/help-guide/09-customer-warehouse.png',
        imageAlt: 'Musteri depo goruntusu',
        caption: 'Musteri veya depo tanim ekranlari',
      },
    ],
  },
  {
    id: 'supply-marketplace',
    number: '06',
    title: 'Tedarik, Teklif ve Dis Kaynak Surecleri',
    icon: Workflow,
    summary: 'Dis kaynak surecler, teklif toplama, pazar yeri ve aktif is akislarinin yonetildigi bolumdur.',
    audience: ['Dispozisyon Uzmanı', 'Dis surucu operasyonu', 'Planlama'],
    paths: ['/supply', '/offer-portal', '/bid-marketplace', '/my-bids', '/active-jobs'],
    modules: ['Tedarik Yonetimi', 'Teklif Portali', 'Teklif Pazari', 'Tekliflerim', 'Aktif Islerim'],
    tasks: ['Yeni talep olusturun veya gelen talebi inceleyin.', 'Teklif toplama surecini baslatin.', 'Dis surucu tekliflerini izleyin.', 'Aktif ise donusen kayitlari takibe alin.'],
    support: ['Teklif gorunmeme sorununda kaydin durumunun teklif toplamaya uygun oldugu kontrol edilir.', 'Dis surucu tarafinda teklif listesi bos ise rol ve atama iliskisi incelenir.'],
    visuals: [
      {
        image: '/help-guide/10-supply-marketplace.png',
        imageAlt: 'Tedarik ve teklif goruntusu',
        caption: 'Tedarik veya teklif portali penceresi',
      },
    ],
  },
  {
    id: 'finance',
    number: '07',
    title: 'Finans, Masraf ve Muhasebe',
    icon: DollarSign,
    summary: 'Masraf girislerinden odeme emrine kadar olan finansal akis bu bolumde yonetilir.',
    audience: ['Muhasebe', 'Admin', 'Operasyon', 'Help desk'],
    paths: ['/expense-submission', '/expense-approval', '/expense-categories', '/accounting-workflow', '/payment-orders', '/cost-analysis'],
    modules: ['Masraf Girisi', 'Masraf Onayi', 'Masraf Kategorileri', 'Dis Tedarik Muhasebe', 'Odeme Emirleri', 'Maliyet Analizi'],
    tasks: ['Masraf olusturun veya ekleri kontrol edin.', 'Onay veya red islemi yapin.', 'Muhasebe akisini ve odeme emirlerini izleyin.', 'Maliyet dagilimlarini analiz edin.'],
    support: ['Masraf listesi gelmiyorsa tarih, kullanici ve durum filtreleri kontrol edilir.', 'Odeme emri farklarinda kaynak masraf ve toplam tutar karsilastirilir.'],
    visuals: [
      {
        image: '/help-guide/11-finance.png',
        imageAlt: 'Finans ve muhasebe goruntusu',
        caption: 'Masraf veya muhasebe penceresi',
      },
    ],
  },
  {
    id: 'reports',
    number: '08',
    title: 'Raporlar ve Analitik',
    icon: BarChart3,
    summary: 'Yonetim, operasyon ve finans ekipleri icin rapor sekmeleri merkezi analiz katmanini olusturur.',
    audience: ['Yonetim', 'Admin', 'Dispozisyon Uzmanı', 'Muhasebe'],
    paths: ['/reports'],
    modules: ['Dashboard Ozeti', 'Donem Karsilastirma', 'Tahmin ve Analitik', 'Operasyon Ozeti', 'Finansal Rapor', 'Surucu Performans', 'Arac Performans', 'Musteri Analizi', 'Guzergah Analizi', 'Teklif Analizi', 'Zamaninda Teslimat', 'Muhasebe ve Odeme', 'Bekleme Sureleri', 'Ozel Rapor Olusturucu'],
    tasks: ['Rapor sekmesini secin.', 'Tarih araligi ve filtreleri tanimlayin.', 'Grafik, tablo ve disa aktarma ozelliklerini kullanin.'],
    support: ['Rapor bossa once tarih filtresi, sonra kaynak veri modulu kontrol edilir.', 'Karsilastirma farklarinda ayni tarih araligi ve ayni filtre seti kullanildigindan emin olun.'],
    visuals: [
      {
        image: '/help-guide/04-reports.png',
        imageAlt: 'Raporlar ekran goruntusu',
        caption: 'Raporlar ve analitik penceresi',
        callouts: [
          { x: 14, y: 18, title: '1', text: 'Rapor sekmeleri farkli analiz tiplerine gecis saglar.' },
          { x: 57, y: 21, title: '2', text: 'Tarih ve filtre paneli rapor sonucunu belirler.' },
          { x: 74, y: 48, title: '3', text: 'Grafik ve ozet kartlar karar destek verisini sunar.' },
        ],
      },
    ],
  },
  {
    id: 'communication',
    number: '09',
    title: 'Iletisim, Destek ve Entegrasyonlar',
    icon: MessageCircle,
    summary: 'Destek merkezi, WhatsApp ve push testleri ile operasyonel iletisim surecleri bu grupta toplanir.',
    audience: ['Help desk', 'Admin', 'Operasyon'],
    paths: ['/support-center', '/help-desk-guide', '/whatsapp-send', '/whatsapp-web', '/push-notifications', '/test-stokbar'],
    modules: ['Destek Merkezi', 'Kullanim Kilavuzu', 'WhatsApp API', 'WhatsApp Web', 'Push Bildirim Testi', 'Stokbar Test Ekrani'],
    tasks: ['Destek kayitlarini okuyun ve yanitlayin.', 'WhatsApp oturum veya API durumunu kontrol edin.', 'Push bildirimlerini test edin.', 'Entegrasyon ekranlarinda servis cevaplarini dogrulayin.'],
    support: ['Mesajlasma sorunlarinda thread, oturum ve servis durumu birlikte incelenir.', 'Entegrasyon erisim hatalarinda URL, ag erisimi ve sertifika durumlari kontrol edilir.'],
    visuals: [
      {
        image: '/help-guide/12-communication.png',
        imageAlt: 'Destek merkezi veya iletisim goruntusu',
        caption: 'Destek merkezi ve iletisim penceresi',
      },
    ],
  },
  {
    id: 'system-governance',
    number: '10',
    title: 'Sistem Tanimlari, Guvenlik ve Yonetim',
    icon: Settings2,
    summary: 'Sistem davranisini belirleyen tanim ekranlari, guvenlik kayitlari ve yonetsel bakim ekranlari bu bolumdedir.',
    audience: ['Admin', 'Yetkili teknik ekip', 'Help desk yoneticisi'],
    paths: ['/supply-metadata', '/contract-management', '/api-key-management', '/share-targets', '/user-management', '/role-management', '/system-logs', '/version-history', '/backup-management', '/settings'],
    modules: ['Form Alan Yonetimi', 'Sozlesme Yonetimi', 'API Anahtarlari', 'Paylasim Hedefleri', 'Kullanici Yonetimi', 'Rol Yonetimi', 'Sistem Loglari', 'Versiyon Gecmisi', 'Yedekleme Yonetimi', 'Sistem Ayarlari'],
    tasks: ['Yetki ve rol konfigurasyonlarini yonetin.', 'API anahtari veya paylasim hedefi tanimlayin.', 'Log, surum ve yedek ekranlarindan teknik takibi yapin.'],
    support: ['Menu erisimi veya aksiyon yetkisi sorunlari once rol yonetiminden kontrol edilir.', 'Teknik olaylarda sistem loglari ve versiyon gecmisi birlikte okunmalidir.'],
    visuals: [
      {
        image: '/help-guide/05-user-role-management.png',
        imageAlt: 'Kullanici ve rol yonetimi ekran goruntusu',
        caption: 'Kullanici ve rol yonetimi',
        callouts: [
          { x: 24, y: 22, title: '1', text: 'Kullanici listesi hesaplari ve durumlarini merkezi olarak gosterir.' },
          { x: 73, y: 24, title: '2', text: 'Rol bazli yetki duzenlemeleri bu alanlardan kontrol edilir.' },
          { x: 68, y: 60, title: '3', text: 'Erisim problemlerinde once kullanici ve rol kaydi birlikte incelenmelidir.' },
        ],
      },
      {
        image: '/help-guide/13-system-governance.png',
        imageAlt: 'Sistem loglari veya ayarlar goruntusu',
        caption: 'Sistem loglari ve yonetsel ekranlar',
      },
      {
        image: '/help-guide/17-settings.png',
        imageAlt: 'Sistem ayarlari goruntusu',
        caption: 'Sistem ayarlari penceresi',
      },
    ],
  },
  {
    id: 'mobile-driver',
    number: '11',
    title: 'Mobil Surucu Uygulamasi',
    icon: Smartphone,
    summary: 'Mobil uygulama surucunun teklif, sevkiyat, belge, guvenlik ve destek sureclerini tek noktada yonetir.',
    audience: ['Ic surucu', 'Dis surucu', 'Mobil destek ekibi'],
    paths: ['/driver-mobile-auth', '/driver-mobile-home', '/driver-mobile-dashboard', '/driver-mobile-profile', '/driver-mobile-notifications', '/driver-mobile-earnings', '/driver-mobile-history', '/driver-mobile-documents', '/driver-mobile-offer/:id', '/driver-mobile-notifications-settings', '/driver-mobile-security', '/driver-mobile-shipment-tracking/:shipmentId', '/driver-mobile-assignments', '/driver-mobile-support', '/driver-mobile-empty-return', '/driver-application', '/contact'],
    modules: ['Mobil Giris', 'Mobil Ana Ekran', 'Sevkiyatlar', 'Profil', 'Bildirimler', 'Kazanc', 'Gecmis', 'Belgeler', 'Teklif Detay', 'Bildirim Ayarlari', 'Guvenlik', 'Sevkiyat Takibi', 'Atamalar', 'Destek Merkezi', 'Bos Donus', 'Surucu Basvuru', 'Iletisim'],
    tasks: ['Telefon numarasi ile giris yapin.', 'Teklifleri ve aktif isleri izleyin.', 'Belge veya destek surecini mobilde tamamlayin.', 'Bildirim ve guvenlik ayarlarini yonetin.'],
    support: ['Mobilde ekran acilmiyorsa oturum ve cihaz izinleri birlikte kontrol edilir.', 'Bildirim sorunlarinda izin, push token ve test ekranlari incelenir.'],
    visuals: [
      {
        image: '/help-guide/06-mobile-auth.png',
        imageAlt: 'Mobil giris ekran goruntusu',
        caption: 'Mobil giris penceresi',
        callouts: [
          { x: 50, y: 37, title: '1', text: 'Telefon numarasi ile dogru mobil kullanici belirlenir.' },
          { x: 51, y: 60, title: '2', text: 'Devam butonu mevcut kullaniciyi ana ekrana, yeniyi kayda yonlendirir.' },
          { x: 48, y: 17, title: '3', text: 'Mobil uygulama surucu odakli sade bir akisla calisir.' },
        ],
      },
      {
        image: '/help-guide/18-mobile-documents.png',
        imageAlt: 'Mobil belgeler goruntusu',
        caption: 'Mobil belgeler penceresi',
      },
    ],
  },
  {
    id: 'mobile-home-flow',
    number: '12',
    title: 'Mobil Ana Ekran ve Gunluk Kullanim',
    icon: MonitorSmartphone,
    summary: 'Surucunun gunluk kullanimda en sik gordugu ekran; teklifler, bildirimler ve sevkiyat gecislerini uzerinde toplar.',
    audience: ['Ic surucu', 'Dis surucu', 'Mobil destek'],
    paths: ['/driver-mobile-home', '/driver-mobile-dashboard'],
    modules: ['Mobil teklif kartlari', 'Bildirim gecisi', 'Profil gecisi', 'Sevkiyat detay gecisi'],
    tasks: ['Teklif kartina dokunup detay acin.', 'Bildirim sayfasina gecin.', 'Gun icindeki aktif akislarin durumunu takip edin.'],
    support: ['Kartlar bos geliyorsa kullanici tipi ve atama iliskisi kontrol edilir.', 'Detay ekranina gecis yoksa ilgili teklif veya sevkiyat verisi incelenir.'],
    visuals: [
      {
        image: '/help-guide/07-mobile-home.png',
        imageAlt: 'Mobil ana ekran goruntusu',
        caption: 'Mobil ana ekran',
        callouts: [
          { x: 52, y: 13, title: '1', text: 'Header alaninda kullanici bilgisi ve hizli aksiyonlar yer alir.' },
          { x: 48, y: 36, title: '2', text: 'Teklif veya is kartlari surucunun gorecegi ana is listesidir.' },
          { x: 49, y: 86, title: '3', text: 'Alt gezinme ile mobil moduller arasi hizli gecis yapilir.' },
        ],
      },
      {
        image: '/help-guide/19-mobile-assignments.png',
        imageAlt: 'Mobil atamalar goruntusu',
        caption: 'Mobil atamalar penceresi',
      },
    ],
  },
  {
    id: 'helpdesk-checklist',
    number: '13',
    title: 'Help Desk Tani ve Eskalasyon Rehberi',
    icon: CircleHelp,
    summary: 'Ilk seviye destek personelinin talebi dogru siniflandirip teknik ekibe temiz aktarabilmesi icin kullanilir.',
    audience: ['Help desk', 'Takim lideri', 'Operasyon destek'],
    paths: ['/support-center', '/system-logs', '/role-management', '/user-management'],
    modules: ['Talep toplama', 'Ilk seviye kontrol', 'Log inceleme', 'Eskalasyon'],
    tasks: ['Kullanici rolu, ekran adi ve hata metnini toplayin.', 'Sorunun web mi mobil mi oldugunu netlestirin.', 'Yetki, filtre, veri ve entegrasyon kontrollerini sirasiyla yapin.'],
    support: ['Tum kullanicilari etkileyen erisim sorunlari dogrudan teknik ekibe aktarilmalidir.', 'Veri kaybi, yanlis tutar veya entegrasyon kesintisi acil eskalasyon kapsamindadir.'],
  },
];

export const filterGuideSections = (query: string) => {
  const normalized = query.trim().toLocaleLowerCase('tr-TR');
  if (!normalized) return guideSections;

  return guideSections.filter((section) => {
    const haystack = [
      section.number,
      section.title,
      section.summary,
      ...section.audience,
      ...section.paths,
      ...section.modules,
      ...section.tasks,
      ...section.support,
      ...(section.visuals || []).map((item) => item.caption),
    ]
      .join(' ')
      .toLocaleLowerCase('tr-TR');

    return haystack.includes(normalized);
  });
};
